import { useMemo } from "react";

import { onGetStoredViewListForWorkspace } from "@/domain/view/lib/on-get-stored-view-list-for-workspace.react-query";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { workspaceByUrlQueryDocument } from "./workspace.gql";

export interface UseGetWorkspaceByUrlProps {
  workspaceUrl?: string;
  enabled?: boolean;
}

export const useWorkspaceGetByUrl = ({ workspaceUrl, enabled = true }: UseGetWorkspaceByUrlProps) => {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.workspaces.byUrl(workspaceUrl ?? ""),
    queryFn: async () => {
      if (!workspaceUrl) {
        throw new Error("Workspace URL is required");
      }
      const client = createOperationClient("workspaceByUrl");
      const data = await client.request(workspaceByUrlQueryDocument, { input: workspaceUrl });
      return data.workspaceByUrl;
    },
    enabled: enabled && !!workspaceUrl,
    staleTime: 5 * 60 * 1000, // 5 minutes for individual items
  });

  const workspace = useMemo(() => {
    const result = queryResult.data ?? null;

    // Handle stored view list processing when data is available
    if (result?.storedViewList) {
      onGetStoredViewListForWorkspace({
        queryClient,
        workspaceId: result.id,
        storedViewList: result.storedViewList,
      });
    }

    return result;
  }, [queryResult.data, queryClient]);

  return {
    workspace,
    loading: queryResult.isLoading,
    error: queryResult.error,
    refetch: queryResult.refetch,
    queryResult,
  };
};
